// src/Form/index.tsx
import React, {
	useRef,
	useEffect,
	useState,
	useMemo,
	useCallback,
} from "react";
import { FIELD_TYPES, VALIDATION_TYPES } from "./constants";
import TemplateRenderer from "./TemplateRenderer";
import DynamicStepper from "./DynamicStepper";
import { ApplicationForm } from "../models";
import * as BoxModule from "@mui/material/Box/index.js";
import ImageHolder from "./components/ImageHolder";
import { convertMomentDateToString } from "../utils/AppDateFormatter";
import moment, { Moment } from "moment";
import { fetchSignedUrl } from "../utils/SessionStorageManager";
import { resolveDefault } from "../utils/moduleInterop";

const Box = resolveDefault<any>(BoxModule);

const getSafeStep = (step: number, totalSteps: number) => {
	if (totalSteps <= 0) {
		return 0;
	}
	if (!Number.isFinite(step)) {
		return 0;
	}
	return Math.min(Math.max(step, 0), totalSteps - 1);
};

interface DynamicFormProps {
	application: ApplicationForm;
	cbSubmit: (
		values: Record<string, any>,
		currentStep: number,
		isFinalSubmit: boolean,
	) => void;
	formId: string;
	// Optional tenant identifier for multi-tenant deployments. Not read or
	// acted upon yet — purely additive type plumbing for later work.
	tenantId?: string;
	setIsValid: (isValid: boolean) => void;
	// onFinalSubmit?: () => void;
	FinalSubmitComponent?: React.ComponentType<{
		application: ApplicationForm;
	}>;
	// id?: string; not in use
	profile?: any;
	currentStep: number;
}

const DynamicForm: React.FC<DynamicFormProps> = (props) => {
	const {
		application,
		cbSubmit,
		formId,
		// onFinalSubmit,
		profile,
		FinalSubmitComponent,
		currentStep,
	} = props;

	const submitFormRef = useRef<() => void>(() => {});
	const templates = application.getTemplateList();
	const totalSteps = templates.length;
	const [activeStep, setActiveStep] = useState(() =>
		getSafeStep(currentStep, totalSteps)
	);
	const [isFinalSubmit, setIsFinalSubmit] = useState(
		application.isFinalSubmit(),
	);
	const [imageDownloadURL, setImageDownloadURL] = useState<string>("");
	const [validationErrorMsg, setValidationErrorMsg] = useState<string>("");

	const onValidationError = useCallback(
		(hasErrors: boolean, errorFields: string[]) => {
			if (hasErrors) {
				setValidationErrorMsg(
					`Please fix the following field${errorFields.length > 1 ? "s" : ""}: ${errorFields.join(", ")}`,
				);
			} else {
				setValidationErrorMsg("");
			}
		},
		[],
	);

	// Memoize flattened values
	const flattenedValues = useMemo(() => {
		return application.getFlatternValues();
	}, [application]);

	useEffect(() => {
		setActiveStep((previousStep) => {
			const nextStep = getSafeStep(currentStep, totalSteps);
			return previousStep === nextStep ? previousStep : nextStep;
		});
	}, [currentStep, totalSteps]);

	const safeActiveStep = getSafeStep(activeStep, totalSteps);

	useEffect(() => {
		const template = templates[safeActiveStep];
		if (!template) {
			setImageDownloadURL("");
			return;
		}
		const keyOrFilename = application.getHeaderImage(template.id);

		fetchSignedUrl(keyOrFilename, "/api/get_s3_form_fileURI")
			.then((url) => {
				setImageDownloadURL(url);
			})
			.catch((error) => {
				console.error("Error retrieving URL:", error);
			});
	}, [safeActiveStep, templates, application]);

	if (isFinalSubmit) {
		const submittedOn = application.submittedOn;

		if (FinalSubmitComponent) {
			return <FinalSubmitComponent application={application} />;
		} else {
			if (submittedOn) {
				return (
					"Form Submitted, We received your last response as of " +
					convertMomentDateToString(moment(submittedOn)) +
					" ફોર્મ સફળતાપૂર્વક સબમિટ કરવામાં આવ્યું છે"
				);
			}
			return "Form Submitted";
		}
	}

	const template = templates[safeActiveStep];

	if (!template) {
		return (
			<Box
				sx={{
					minHeight: "100%",
					marginLeft: "auto",
					marginRight: "auto",
				}}
			>
				{application.name}
			</Box>
		);
	}

	const cbSubmitTmp = (values: any) => {
		console.log("cbSubmitTmp");
		setValidationErrorMsg("");

		template.update_default_values(values);
		application.updateTemplate(activeStep, template);

		application.nextStep();
		application.submit();
		const ttlSteps = application.getStepList().length;

		const getCurrentStep = application.getCurrentStep();
		cbSubmit(values, getCurrentStep, application.activeStep === ttlSteps);
		setActiveStep(getCurrentStep);
		setIsFinalSubmit(application.isFinalSubmit());
	};

	const handleNext = () => {
		submitFormRef.current();
	};

	const handleBack = () => {
		application.prevStep();
		setActiveStep(application.getCurrentStep());
	};

	return (
		<Box
			sx={{
				minHeight: "100%",
				marginLeft: "auto",
				marginRight: "auto",
			}}
		>
			<ImageHolder alt="" image={imageDownloadURL} />
			<DynamicStepper
				application_name={application.name}
				totalSteps={totalSteps}
				activeStep={safeActiveStep}
				handleNext={handleNext}
				handleBack={handleBack}
				steps={application.getStepList()}
				validationErrorMsg={validationErrorMsg}
				nextButtonName={
					template.nextButtonName === undefined
						? ""
						: template.nextButtonName
				}
				backButtonName={
					template.backButtonName === undefined
						? ""
						: template.backButtonName
				}
			>
				<TemplateRenderer
					cbSubmit={cbSubmitTmp}
					template={template}
					formId={formId}
					submitFormRef={submitFormRef}
					profile={profile}
					formValues={flattenedValues}
					onValidationError={onValidationError}
				/>
			</DynamicStepper>
		</Box>
	);
};

export { FIELD_TYPES, VALIDATION_TYPES, DynamicForm };
